import { type ComponentProps, type ReactNode, useState } from 'react';
import { Button } from '@startuptools/ui/Button';
import { type ColorPaletteProp } from '@mui/joy/styles';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Trans } from '@lingui/react';
import { isPromise } from '../../helpers/is-promise';
import { DialogTitle } from '@startuptools/ui/joy/DialogTitle';
import { DialogContent } from '@startuptools/ui/joy/DialogContent';
import { DialogActions } from '@startuptools/ui/joy/DialogActions';
import { Box } from '@startuptools/ui/Box';

export const AlertDialogContent = ({
  title,
  onAccept,
  onCancel,
  onDecline,
  onAcceptButtonColor = 'primary',
  children,
  acceptButtonText = <Trans id="Accept" />,
  declineButtonText = <Trans id="Decline" />,
  cancelButtonText = <Trans id="Cancel" />,
}: Pick<ComponentProps<typeof ModalDialog>, 'children'> & {
  title: ReactNode;
  onAccept: () => Promise<unknown> | unknown;
  onDecline?: () => Promise<unknown> | unknown;
  onCancel?: () => void;
  onAcceptButtonColor?: ColorPaletteProp;
  acceptButtonText?: ReactNode;
  declineButtonText?: ReactNode;
  cancelButtonText?: ReactNode;
}) => {
  const [loadingAccept, setLoadingAccept] = useState(false);
  const [loadingDecline, setLoadingDecline] = useState(false);

  const handleAccept = async () => {
    const ret = onAccept();
    if (isPromise(ret)) {
      setLoadingAccept(true);
      return ret.finally(() => setLoadingAccept(false));
    }
  };

  const handleDecline = async () => {
    const ret = onDecline?.();
    if (isPromise(ret)) {
      setLoadingDecline(true);
      return ret.finally(() => setLoadingDecline(false));
    }
  };

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions orientation="horizontal">
        {onCancel && (
          <Button onClick={onCancel} disabled={loadingAccept || loadingDecline} variant="plain">
            {cancelButtonText}
          </Button>
        )}
        {onDecline && (
          <Button onClick={handleDecline} disabled={loadingAccept} loading={loadingDecline} variant="plain">
            {declineButtonText}
          </Button>
        )}
        <Box>
          <Button
            onClick={handleAccept}
            disabled={loadingDecline}
            loading={loadingAccept}
            color={onAcceptButtonColor}
            variant="solid"
          >
            {acceptButtonText}
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};

export const AlertDialog = ({
  onCancel,
  maxWidth,
  children,
  ...props
}: Pick<ComponentProps<typeof ModalDialog>, 'maxWidth' | 'children'> & ComponentProps<typeof AlertDialogContent>) => {
  return (
    <Modal open onClose={onCancel}>
      <ModalDialog maxWidth={maxWidth}>
        <AlertDialogContent {...props} onCancel={onCancel}>
          {children}
        </AlertDialogContent>
      </ModalDialog>
    </Modal>
  );
};
